import { getApiUrl } from '@/config/api'
import { buildFetcher } from '@/services/api'

import type {
  LatestProficiencyResponse,
  DetailedProficiencyResponse,
  AssessmentAvailabilityResponse,
  CurrentSessionStatusResponse,
  SkillScore,
} from '@/types/assessment'

export interface AssessmentHistoryItem {
  id: string
  level_achieved: string
  raw_score: number | null
  max_score: number | null
  percentage: number
  pa_score: number | null
  assessment_version: string
  recorded_at: string
  /** Per-skill breakdown from the same assessment. Empty for pre-multi-skill assessments. */
  skill_scores?: readonly SkillScore[]
}

export interface AssessmentHistoryResponse {
  data: AssessmentHistoryItem[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

export const assessmentService = {
  async getAvailability(getToken: () => Promise<string | null>): Promise<AssessmentAvailabilityResponse> {
    const token = await getToken()
    if (!token) {
      throw new Error('No authentication token available')
    }

    const response = await fetch(getApiUrl('/assessment/availability'), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to check assessment availability: ${response.status} - ${errorText}`)
    }

    return response.json()
  },

  async exportResultsCsv(userIds: string[], getToken: () => Promise<string | null>): Promise<Blob> {
    const token = await getToken()
    if (!token) {
      throw new Error('No authentication token available')
    }

    const response = await fetch(getApiUrl('/assessment/export/results'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'text/csv',
      },
      body: JSON.stringify({ user_ids: userIds }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to export results CSV: ${response.status} - ${errorText}`)
    }

    return response.blob()
  },

  getAssessmentHistoryUrl: (params: URLSearchParams) => getApiUrl(`/assessment/history?${params.toString()}`),
  getAssessmentHistory: (params: URLSearchParams) =>
    buildFetcher<AssessmentHistoryResponse>(assessmentService.getAssessmentHistoryUrl(params)),

  async getAssessmentHistoryForUser(
    userId: string,
    getToken: () => Promise<string | null>,
    opts: {
      limit: number
      offset: number
      sortBy: 'recorded_at' | 'level_achieved' | 'percentage'
      sortOrder: 'asc' | 'desc'
    }
  ): Promise<AssessmentHistoryResponse> {
    const token = await getToken()
    if (!token) {
      throw new Error('No authentication token available')
    }

    const params = new URLSearchParams({
      user_id: userId,
      limit: String(opts.limit),
      offset: String(opts.offset),
      sort_by: opts.sortBy,
      sort_order: opts.sortOrder,
    })

    const response = await fetch(getApiUrl(`/assessment/admin/history?${params.toString()}`), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to fetch assessment history for user: ${response.status} - ${errorText}`)
    }

    return response.json()
  },

  async getLatestProficiencyForUser(
    userId: string,
    getToken: () => Promise<string | null>
  ): Promise<LatestProficiencyResponse | null> {
    const token = await getToken()
    if (!token) {
      throw new Error('No authentication token available')
    }

    const params = new URLSearchParams({ user_id: userId })
    const response = await fetch(getApiUrl(`/assessment/admin/latest-proficiency?${params.toString()}`), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to fetch latest proficiency for user: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    return data as LatestProficiencyResponse | null
  },
  async getLatestProficiency(getToken: () => Promise<string | null>): Promise<LatestProficiencyResponse | null> {
    const token = await getToken()
    if (!token) {
      throw new Error('No authentication token available')
    }

    const response = await fetch(getApiUrl('/assessment/latest-proficiency'), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to fetch latest proficiency: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    // If the API returns null (no proficiency achieved), return null
    return data as LatestProficiencyResponse | null
  },

  async getDetailedLatestProficiency(
    getToken: () => Promise<string | null>
  ): Promise<DetailedProficiencyResponse | null> {
    const token = await getToken()
    if (!token) {
      throw new Error('No authentication token available')
    }

    const response = await fetch(getApiUrl('/assessment/latest-proficiency/details'), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to fetch detailed proficiency: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    return data as DetailedProficiencyResponse | null
  },

  async getCurrentSessionStatus(getToken: () => Promise<string | null>): Promise<CurrentSessionStatusResponse> {
    const token = await getToken()
    if (!token) {
      throw new Error('No authentication token available')
    }

    const response = await fetch(getApiUrl('/assessment/sessions/current'), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to fetch current session status: ${response.status} - ${errorText}`)
    }

    return response.json()
  },

  async getAssessmentResult(proficiencyId: string, getToken: () => Promise<string | null>): Promise<any> {
    const token = await getToken()
    if (!token) {
      throw new Error('No authentication token available')
    }

    const response = await fetch(getApiUrl(`/assessment/results/${proficiencyId}`), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to get assessment result: ${error}`)
    }

    return response.json()
  },

  // =========================================================================
  // Admin Override Methods.
  // =========================================================================

  /**
   * Get the active assessment override for a user (admin only)
   */
  async getOverrideForUser(
    userId: string,
    getToken: () => Promise<string | null>
  ): Promise<AssessmentOverrideResponse | null> {
    const token = await getToken()
    if (!token) {
      throw new Error('No authentication token available')
    }

    const response = await fetch(getApiUrl(`/admin/users/${userId}/assessment-override`), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Not authorized to manage assessment overrides')
      }
      const error = await response.text()
      throw new Error(`Failed to get assessment override: ${error}`)
    }

    const data = await response.json()
    return data // Can be null if no active override
  },

  /**
   * Grant a PA cooldown override for a user (admin only)
   */
  async grantOverride(
    userId: string,
    reason: string | undefined,
    getToken: () => Promise<string | null>
  ): Promise<AssessmentOverrideResponse> {
    const token = await getToken()
    if (!token) {
      throw new Error('No authentication token available')
    }

    const response = await fetch(getApiUrl(`/admin/users/${userId}/assessment-override`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason: reason || null }),
    })

    if (!response.ok) {
      if (response.status === 409) {
        throw new Error('User already has an active override')
      }
      const error = await response.text()
      throw new Error(`Failed to grant override: ${error}`)
    }

    return response.json()
  },

  /**
   * Revoke an active PA cooldown override (admin only)
   */
  async revokeOverride(
    userId: string,
    reason: string | undefined,
    getToken: () => Promise<string | null>
  ): Promise<AssessmentOverrideResponse> {
    const token = await getToken()
    if (!token) {
      throw new Error('No authentication token available')
    }

    const response = await fetch(getApiUrl(`/admin/users/${userId}/assessment-override/revoke`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason: reason || null }),
    })

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('No active override found')
      }
      const error = await response.text()
      throw new Error(`Failed to revoke override: ${error}`)
    }

    return response.json()
  },
}

// Types for assessment override
export type AssessmentOverrideResponse = {
  readonly id: string
  readonly user_id: string
  readonly created_by_id: string
  readonly created_by_name: string | null
  readonly reason: string | null
  readonly created_at: string
  readonly is_active: boolean
  readonly used_at: string | null
  readonly assessment_session_id: string | null
  readonly revoked_at: string | null
  readonly revoked_by_id: string | null
  readonly revoked_by_name: string | null
  readonly revoked_reason: string | null
}
